import { useState } from 'react';
import { Text, View } from 'react-native';
import { StyleSheet } from 'react-native-unistyles';
import Toast from 'react-native-toast-message';
import Avatar from '@/components/People/Avatar';
import Unicon from '@/components/General/Unicon';
import SheetSelector from '@/components/General/inputs/SheetSelector';
import SolidButton from '@/components/General/SolidButton';
import { Query } from '@/utils/react-query/Queries';
import { Mutation } from '@/utils/react-query/Mutations';

/**
 * CaregiverRow — the person's primary caregiver with manager reassignment.
 *
 * The primary caregiver is a default responsibility, not exclusive read
 * access — the whole team sees this row. Only 'Manage Follow Up' holders get
 * the Assign/Change picker (server enforces regardless).
 */
const CaregiverRow = ({ organizationID, personID, caregiverPersonID, canManage }) => {
    const [pickerOpen, setPickerOpen] = useState(false);

    const { data: caregiver } = Query.people.basicById({
        id: caregiverPersonID,
        options: { enabled: !!caregiverPersonID },
    });

    const mutation = Mutation.followUp.setPrimaryCaregiver({
        onSuccess: (data) => {
            if ((data?.pendingTaskCount || 0) > 0) {
                Toast.show({
                    type: 'info',
                    text2: `Connector updated. ${data.pendingTaskCount} open task${data.pendingTaskCount === 1 ? '' : 's'} still need${data.pendingTaskCount === 1 ? 's' : ''} reassignment.`,
                });
            } else {
                Toast.show({ type: 'success', text2: 'Connector updated' });
            }
        },
        onError: (error) => {
            if (!error?.expected) Toast.show({ type: 'error', text2: 'Failed to update the connector.' });
        },
    });

    const handleSelect = (person) => {
        if (!person?.id || person.id === caregiverPersonID) return;
        mutation.mutate({ organizationID, personID, caregiverPersonID: person.id });
    };

    return (
        <View style={styles.row} testID="follow-up-caregiver-row">
            <View style={styles.iconWrap}>
                <Unicon name="user-round-arrow-left" size={16} colorVariant="paths" />
            </View>
            <View style={styles.body}>
                <Text style={styles.label}>Primary Connector</Text>
                {caregiverPersonID ? (
                    <View style={styles.caregiverLine}>
                        <Avatar size={22} profile={caregiver} />
                        <Text style={styles.value}>
                            {caregiver ? `${caregiver.firstName || ''} ${caregiver.lastName || ''}`.trim() : 'Loading…'}
                        </Text>
                    </View>
                ) : (
                    <Text style={styles.unassigned}>Unassigned</Text>
                )}
            </View>
            {canManage && (
                <View style={styles.actions}>
                    <SheetSelector
                        type="people"
                        title="Primary Connector"
                        titleOnly
                        placeholder={caregiverPersonID ? 'Change' : 'Assign'}
                        value={caregiver}
                        onChange={handleSelect}
                        open={pickerOpen}
                        onOpen={() => setPickerOpen(true)}
                        onClose={() => setPickerOpen(false)}
                        excludeIds={personID ? [personID] : []}
                        testID="follow-up-caregiver-picker"
                    >
                        <SolidButton
                            testID="follow-up-connector-assign"
                            size="sm"
                            variant="outlined"
                            type="secondary"
                            text={caregiverPersonID ? 'Change' : 'Assign'}
                            onPress={() => setPickerOpen(true)}
                        />
                    </SheetSelector>
                    {!!caregiverPersonID && (
                        <SolidButton
                            testID="follow-up-caregiver-clear"
                            size="sm"
                            variant="outlined"
                            type="textSecondary"
                            text="Clear"
                            onPress={() => mutation.mutate({ organizationID, personID, caregiverPersonID: null, reason: 'Cleared via app' })}
                        />
                    )}
                </View>
            )}
        </View>
    );
};

const styles = StyleSheet.create(({ colors, fonts }) => ({
    row: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 10,
    },
    iconWrap: {
        width: 32,
        height: 32,
        borderRadius: 8,
        backgroundColor: colors.pathsContainer,
        alignItems: 'center',
        justifyContent: 'center',
    },
    body: { flex: 1, minWidth: 0 },
    label: { fontSize: 11, color: colors.textSecondary, fontFamily: fonts.medium },
    caregiverLine: { flexDirection: 'row', alignItems: 'center', gap: 6, marginTop: 2 },
    value: { fontSize: 14, fontFamily: fonts.bold, color: colors.text },
    unassigned: { fontSize: 14, color: colors.textSecondary, fontStyle: 'italic', marginTop: 2 },
    actions: { flexDirection: 'row', alignItems: 'center', gap: 8 },
}));

export default CaregiverRow;
